import React, { ComponentType } from 'react';

import { useTranslation } from 'react-i18next';

import { UITooltip } from '@shared/ui';

import {
    EmailIcon,
    FacebookIcon,
    HelpCenterIcon,
    InstagramIcon,
    TelegramIcon,
    XIcon,
} from './AssistantContactIcons';
import cls from './KnowledgeAssistant.module.css';

const FALLBACK_URL = 'https://app.smetter.com';

const SOCIAL_LINKS: Array<{
    name: string;
    urlKey: string;
    labelKey: string;
    Icon: ComponentType;
}> = [
    {
        name: 'telegram',
        urlKey: 'KnowledgeAssistant.contacts.telegramUrl',
        labelKey: 'KnowledgeAssistant.contacts.telegram',
        Icon: TelegramIcon,
    },
    {
        name: 'x',
        urlKey: 'KnowledgeAssistant.contacts.xUrl',
        labelKey: 'KnowledgeAssistant.contacts.x',
        Icon: XIcon,
    },
    {
        name: 'instagram',
        urlKey: 'KnowledgeAssistant.contacts.instagramUrl',
        labelKey: 'KnowledgeAssistant.contacts.instagram',
        Icon: InstagramIcon,
    },
    {
        name: 'facebook',
        urlKey: 'KnowledgeAssistant.contacts.facebookUrl',
        labelKey: 'KnowledgeAssistant.contacts.facebook',
        Icon: FacebookIcon,
    },
];

const safeLocalizedUrl = (value: string) => {
    try {
        const url = new URL(value);
        return url.protocol === 'https:' ? url.toString() : FALLBACK_URL;
    } catch {
        return FALLBACK_URL;
    }
};

export const AssistantContactLinks = () => {
    const { t } = useTranslation();
    const emailAddress = t('KnowledgeAssistant.contacts.emailAddress');

    return (
        <aside
            className={cls.contactPanel}
            aria-label={t('KnowledgeAssistant.contacts.panelLabel')}
        >
            <div className={cls.contactHeading}>
                <UITooltip
                    label={t('KnowledgeAssistant.contacts.tooltips.helpCenter')}
                    tooltipClasses={cls.contactTooltipContent}
                >
                    <a
                        className={cls.helpCenterButton}
                        href={safeLocalizedUrl(t('KnowledgeAssistant.contacts.helpCenterUrl'))}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={t('KnowledgeAssistant.contacts.tooltips.helpCenter')}
                    >
                        <HelpCenterIcon />
                    </a>
                </UITooltip>
                <span className={cls.contactHeadingText}>
                    {t('KnowledgeAssistant.contacts.helpCenter')}
                </span>
            </div>
            <div className={cls.contactSpacer} />
            <UITooltip
                label={
                    <div className={cls.emailPopover}>
                        <span className={cls.emailPopoverTitle}>
                            {t('KnowledgeAssistant.contacts.tooltips.email')}
                        </span>
                        <a className={cls.emailPopoverAddress} href={`mailto:${emailAddress}`}>
                            {emailAddress}
                        </a>
                    </div>
                }
                classes={cls.contactTooltip}
                tooltipClasses={`${cls.contactTooltipContent} ${cls.emailPopoverContainer}`}
                trigger="click"
            >
                <button
                    type="button"
                    className={cls.contactButton}
                    aria-label={t('KnowledgeAssistant.contacts.email')}
                >
                    <EmailIcon />
                </button>
            </UITooltip>
            {SOCIAL_LINKS.map(({ name, urlKey, labelKey, Icon }) => (
                <UITooltip
                    key={name}
                    label={t(`KnowledgeAssistant.contacts.tooltips.${name}`)}
                    classes={cls.contactTooltip}
                    tooltipClasses={cls.contactTooltipContent}
                >
                    <a
                        className={cls.contactButton}
                        href={safeLocalizedUrl(t(urlKey))}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={t(labelKey)}
                    >
                        <Icon />
                    </a>
                </UITooltip>
            ))}
        </aside>
    );
};
